import ApplicationController from "../application_component_controller";
import { formatBytes } from "hb/lib/number_helpers";
import {
  Announcer,
  countText,
  highlight,
} from "../dropdown_component/combobox";

// Stream picker interaction model:
// - The list shows only projects the user has added (data-added="true").
//   Every project group is pre-rendered so form submission collects all
//   stream_ids[] values, but unadded groups stay hidden via CSS.
// - A DropdownComponent input-trigger menu offers unadded projects. Selecting one flips the group's data-added and auto-checks
//   its unattached streams.
// - Removing a project unchecks its streams and hides the group again.
// - Per-stream checkboxes stay available inside each added group so the
//   user can opt out of either Customer or Internal individually.
// - The search field is a combobox and the menu its listbox. Focus stays in
//   the field: Up/Down move a highlight (aria-activedescendant), typing
//   highlights the first match, Enter adds the highlighted project (or the
//   first one listed), and a live region reports the result count.
export default class extends ApplicationController {
  static targets = [
    "search",
    "searchStatus",
    "searchResult",
    "searchEmpty",
    "group",
    "groupSummary",
    "streamCheckbox",
    "summaryCount",
    "summarySize",
    "emptyState",
  ];

  connect() {
    this.announcer = new Announcer(
      this.hasSearchStatusTarget ? this.searchStatusTarget : null,
    );
    this.groupTargets.forEach((g) => this.updateGroupSummary(g));
    this.filterSearchResults();
    this.updateTotals();
  }

  disconnect() {
    this.announcer.clear();
  }

  searchInput() {
    this.filterSearchResults();
    this.highlightOption(this.visibleOptions()[0] ?? null);
    this.announceCount();
  }

  searchShown() {
    this.announceCount();
  }

  searchHidden() {
    this.highlightOption(null);
    this.announcer.clear();
  }

  searchKeydown(event) {
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp": {
        if (!this.searchTarget.matches("[aria-expanded='true']")) return;
        event.preventDefault();
        this.moveHighlight(event.key === "ArrowDown" ? 1 : -1);
        break;
      }
      case "Enter": {
        if (event.isComposing) return;
        event.preventDefault();
        const visible = this.visibleOptions();
        const option = visible.find((o) => o.classList.contains("active"));
        (option ?? visible[0])?.click();
        break;
      }
    }
  }

  moveHighlight(step) {
    const visible = this.visibleOptions();
    const index = visible.findIndex((o) => o.classList.contains("active"));
    const next =
      index === -1 ? (step > 0 ? visible[0] : null) : visible[index + step];
    if (next) this.highlightOption(next, { scroll: true });
  }

  highlightOption(option, options = {}) {
    highlight(this.searchTarget, this.options(), option, options);
  }

  announceCount() {
    this.announcer.announce(countText(this.visibleOptions().length));
  }

  options() {
    return this.searchResultTargets
      .map((r) => r.querySelector("[role='option']"))
      .filter(Boolean);
  }

  visibleOptions() {
    return this.visibleSearchResults()
      .map((r) => r.querySelector("[role='option']"))
      .filter(Boolean);
  }

  addProject(event) {
    event.preventDefault();
    const li = event.currentTarget.closest(
      "[data-archive-destinations--stream-picker-component-target~='searchResult']",
    );
    const name = li?.dataset.projectName;
    if (name) this.addProjectByName(name);
  }

  removeProject(event) {
    event.preventDefault();
    const group = this.closestGroup(event.currentTarget);
    if (!group) return;

    group.dataset.added = "false";
    this.streamsIn(group).forEach((cb) => {
      if (!cb.disabled) cb.checked = false;
    });

    this.updateGroupSummary(group);
    this.filterSearchResults();
    this.updateTotals();
  }

  streamChanged(event) {
    const group = this.closestGroup(event.target);
    if (group) this.updateGroupSummary(group);
    this.updateTotals();
  }

  addProjectByName(name) {
    const group = this.groupTargets.find((g) => g.dataset.projectName === name);
    if (!group) return;

    group.dataset.added = "true";
    group.parentElement.prepend(group);
    this.streamsIn(group).forEach((cb) => {
      if (!cb.disabled) cb.checked = true;
    });

    this.searchTarget.value = "";
    this.updateGroupSummary(group);
    this.filterSearchResults();
    this.updateTotals();
  }

  streamsIn(groupEl) {
    return Array.from(groupEl.querySelectorAll('input[name="stream_ids[]"]'));
  }

  closestGroup(el) {
    return el.closest(
      "[data-archive-destinations--stream-picker-component-target~='group']",
    );
  }

  updateGroupSummary(groupEl) {
    const boxes = this.streamsIn(groupEl).filter((cb) => !cb.disabled);
    const total = boxes.length;
    const selected = boxes.filter((cb) => cb.checked).length;
    const el = groupEl.querySelector(
      "[data-archive-destinations--stream-picker-component-target~='groupSummary']",
    );
    if (el) el.textContent = `${selected}/${total} selected`;
  }

  updateTotals() {
    const checked = this.streamCheckboxTargets.filter((cb) => cb.checked);
    const bytes = checked.reduce(
      (acc, cb) => acc + (parseInt(cb.dataset.bytes, 10) || 0),
      0,
    );
    if (this.hasSummaryCountTarget)
      this.summaryCountTarget.textContent = String(checked.length);
    if (this.hasSummarySizeTarget)
      this.summarySizeTarget.textContent = formatBytes(bytes);

    const anyAdded = this.groupTargets.some((g) => g.dataset.added === "true");
    if (this.hasEmptyStateTarget)
      this.emptyStateTarget.classList.toggle("d-none", anyAdded);
  }

  filterSearchResults() {
    const query = this.searchTarget.value.trim().toLowerCase();
    const addedNames = new Set(
      this.groupTargets
        .filter((g) => g.dataset.added === "true")
        .map((g) => g.dataset.projectName),
    );
    let visible = 0;
    this.searchResultTargets.forEach((r) => {
      const name = r.dataset.projectName || "";
      const taken = addedNames.has(name);
      const matches = query === "" || name.toLowerCase().includes(query);
      const show = !taken && matches;
      r.classList.toggle("d-none", !show);
      if (show) visible++;
    });
    if (this.hasSearchEmptyTarget)
      this.searchEmptyTarget.classList.toggle("d-none", visible > 0);
  }

  visibleSearchResults() {
    return this.searchResultTargets.filter(
      (r) => !r.classList.contains("d-none"),
    );
  }
}
